import { Box, Typography, useMediaQuery } from "@mui/material";
import { useState } from "react";
import { layoutTokens, loadingTokens } from "@/shared/theme";

const squares = Array.from({ length: 16 }, (_, index) => index);
const ASIDES = [
  "NIKOLA: Knights prefer L-shaped career paths.",
  "NIKOLA: Even queens started as pawns.",
  "NIKOLA: One calm move can change the whole board.",
] as const;

export const loadingAsideFor = (random: () => number = Math.random): string | null => {
  const chance = random();
  if (chance >= 0.08) return null;
  return ASIDES[Math.min(ASIDES.length - 1, Math.floor((chance / 0.08) * ASIDES.length))];
};

export const ChesslaLoadingMark = ({
  label,
  page,
  fullScreen = false,
  fillContent = false,
}: {
  label: string;
  page?: string;
  fullScreen?: boolean;
  fillContent?: boolean;
}) => {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [aside] = useState(() => loadingAsideFor());
  return (
    <Box
      data-loading-scope={fullScreen ? "screen" : fillContent ? "content" : "inline"}
      role="status"
      aria-live="polite"
      aria-label={label}
      sx={{
        alignContent: "center",
        bgcolor: "background.default",
        boxSizing: "border-box",
        color: "text.primary",
        display: "grid",
        height: fullScreen
          ? layoutTokens.screenMinHeight
          : fillContent
            ? { xs: "calc(100dvh - var(--mobile-app-bar-height, 44px))", md: "100%" }
            : undefined,
        justifyItems: "center",
        minHeight: fullScreen
          ? layoutTokens.screenMinHeight
          : fillContent
            ? { xs: "calc(100dvh - var(--mobile-app-bar-height, 44px))", md: "100%" }
            : loadingTokens.minHeight,
        p: loadingTokens.padding,
        width: "100%",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          height: loadingTokens.boardSize,
          mb: loadingTokens.boardGap,
          width: loadingTokens.boardSize,
        }}
      >
        {squares.map((square) => (
          <Box
            key={square}
            sx={{
              animation: reducedMotion
                ? "none"
                : `chessla-loading ${loadingTokens.animation.duration} steps(2, end) infinite`,
              animationDelay: `${(square % 4) * loadingTokens.animation.columnDelayMs}ms`,
              bgcolor: (Math.floor(square / 4) + square) % 2 ? "secondary.main" : "text.primary",
              border: layoutTokens.dividerBorder,
              borderColor: "background.default",
              "@keyframes chessla-loading": { "50%": { opacity: loadingTokens.animation.dimOpacity } },
            }}
          />
        ))}
      </Box>
      <Typography sx={loadingTokens.title}>{page ? `LOADING ${page.toUpperCase()}` : "CHESSLA"}</Typography>
      <Typography sx={{ color: "text.secondary", ...loadingTokens.detail }}>{label.toUpperCase()}</Typography>
      {aside && (
        <Typography sx={{ color: "text.primary", ...loadingTokens.aside, textAlign: "center" }}>{aside}</Typography>
      )}
    </Box>
  );
};
